/**
 * Archive API Service
 * REST: GET/POST /api/archive, GET/DELETE /api/archive/{id}, POST /api/archive/{id}/restore,
 *       GET /api/archive/content/{entityType}/{entityId}
 *
 * Ersetzt die bisherige rein lokale (localStorage-basierte) Archiv-Verwaltung
 * in src/utils/archiveManager.js. Server ist jetzt die Quelle der Wahrheit -
 * siehe TODO_Archiv_Backend.md Abschnitt 0 und 3.
 */

const getBaseUrl = () => process.env.REACT_APP_API_URL || '/api';

const getAuthToken = () => localStorage.getItem('authToken');

const getHeaders = () => {
  const token = getAuthToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

async function apiFetch(endpoint, options = {}) {
  const url = `${getBaseUrl()}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: getHeaders(),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Nicht autorisiert. Bitte erneut anmelden.');
    }
    if (response.status === 403) {
      throw new Error('Zugriff verweigert.');
    }
    if (response.status === 404) {
      throw new Error('Archiv-Eintrag nicht gefunden.');
    }
    if (response.status === 409) {
      throw new Error('Existiert bereits.');
    }
    const errorText = await response.text();
    throw new Error(errorText || `HTTP Error: ${response.status}`);
  }

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  return { success: true };
}

const archiveApi = {
  /**
   * Liste aller Archiv-Einträge des Nutzers, optional gefiltert nach Typ.
   * @param {string|null} entityType - category|bookmark|recipe|shoppingList
   */
  async list(entityType = null) {
    const query = entityType ? `?entityType=${encodeURIComponent(entityType)}` : '';
    return apiFetch(`/archive${query}`);
  },

  /**
   * Legt einen Archiv-Eintrag an.
   * @param {{entityType:string, reason:string, originalId?:string, label?:string, snapshot:object}} entry
   */
  async create(entry) {
    return apiFetch('/archive', {
      method: 'POST',
      body: JSON.stringify(entry),
    });
  },

  /**
   * Entfernt einen Eintrag endgültig aus dem Archiv.
   */
  async remove(id) {
    return apiFetch(`/archive/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Stellt einen Archiv-Eintrag wieder her. Legt serverseitig ein neues
   * Objekt (neue ID) an und entfernt danach den Archiv-Eintrag.
   * @returns {Promise<{success:boolean, restored:object}>}
   */
  async restore(id) {
    return apiFetch(`/archive/${id}/restore`, {
      method: 'POST',
    });
  },

  /**
   * Lädt den gespeicherten Plain-Text-Content-Snapshot einer Entität
   * (z.B. zum Anzeigen, wenn die Originalseite verschwunden ist).
   * @param {string} entityType - bookmark|recipe
   * @param {string|number} entityId - die (frühere) numerische ID des Objekts
   * @returns {Promise<object|null>} null falls (noch) kein Snapshot existiert (404)
   */
  async getContent(entityType, entityId) {
    try {
      return await apiFetch(`/archive/content/${entityType}/${entityId}`);
    } catch (err) {
      if (err.message === 'Archiv-Eintrag nicht gefunden.') {
        return null;
      }
      throw err;
    }
  },
};

export default archiveApi;
